// Hirá: cart drawer "You might also like" carousel.
//
// Carousel: the track is a native scroll-snap row; this keeps the dots in sync and
// lets them jump between slides. Listeners are delegated because the drawer re-renders.
//
// Holding steady: the suggestions are decided when the drawer opens. While it stays
// open, changes made inside the drawer (removing an item, changing a quantity) re-render
// the cart but the carousel is put back as it was, so a product the shopper just removed
// doesn't pop straight back up as a suggestion. The one exception is a product added
// from the carousel itself, which is taken out of it. Changes from outside the drawer
// (an add to cart elsewhere, which then opens it) are accepted as the new starting point.
(() => {
  const DRAWER = '#cart-drawer';

  /* ---------- Carousel ---------- */

  const slides = (track) => [...track.children];

  // Nearest slide to the current scroll position (slides have a gap between them)
  const slideIndex = (track) => {
    const all = slides(track);
    let best = 0;
    all.forEach((slide, i) => {
      if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(all[best].offsetLeft - track.scrollLeft)) best = i;
    });
    return best;
  };

  const syncDots = (root) => {
    const track = root?.querySelector('[data-hira-upsell-track]');
    if (!track) return;
    const active = slideIndex(track);
    root.querySelectorAll('[data-hira-upsell-dot]').forEach((dot, i) => {
      if (i === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  document.addEventListener('click', (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-hira-upsell-dot]') : null;
    if (!dot) return;
    const track = dot.closest('[data-hira-upsell]')?.querySelector('[data-hira-upsell-track]');
    const slide = track && slides(track)[Number(dot.dataset.hiraUpsellDot)];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target instanceof Element ? event.target.closest('[data-hira-upsell-track]') : null;
      if (track) syncDots(track.closest('[data-hira-upsell]'));
    },
    { capture: true, passive: true }
  );

  /* ---------- Holding steady while open ---------- */

  let frozen = null; // slot HTML captured when the drawer opened
  let acceptNextRender = false; // an add from outside the drawer is still landing

  const slot = () => document.querySelector(`${DRAWER} [data-hira-upsell-slot]`);
  const isOpen = () => Boolean(document.querySelector(`${DRAWER} dialog[open]`));

  const capture = () => {
    const current = slot();
    frozen = current ? current.innerHTML : null;
  };

  // Take a product out of the captured carousel and tidy the dots to match
  const withoutProduct = (html, productId) => {
    const holder = document.createElement('div');
    holder.innerHTML = html;
    holder.querySelector(`.hira-upsell__item[data-product-id="${productId}"]`)?.remove();

    const count = holder.querySelectorAll('.hira-upsell__item').length;
    if (count === 0) return '';

    const dots = holder.querySelector('.hira-upsell__dots');
    if (dots && count < 2) {
      dots.remove();
    } else if (dots) {
      [...dots.children].forEach((dot, i) => {
        if (i >= count) return dot.remove();
        dot.setAttribute('aria-label', `Show suggestion ${i + 1} of ${count}`);
        if (i === 0) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    return holder.innerHTML;
  };

  // Opening the drawer doesn't re-render it, so fetch fresh suggestions after it
  // closes; the next open then starts from an up-to-date list
  const refresh = async () => {
    const sectionId = document.querySelector(`${DRAWER} cart-items-component`)?.dataset.sectionId;
    if (!sectionId) return;
    try {
      const response = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const html = (await response.json())[sectionId];
      if (!html || isOpen()) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hira-upsell-slot]');
      const current = slot();
      if (fresh && current && current.innerHTML !== fresh.innerHTML) current.innerHTML = fresh.innerHTML;
    } catch {
      // Keep the current suggestions if the request fails
    }
  };

  const restore = () => {
    const current = slot();
    if (current && frozen !== null && current.innerHTML !== frozen) current.innerHTML = frozen;
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source?.closest(DRAWER)) {
      // Accept re-renders until this outside add has landed, whichever order Horizon
      // opens the drawer and re-renders it in
      acceptNextRender = true;
      const settle = () => setTimeout(() => (acceptNextRender = false), 400);
      if (event.promise) event.promise.then(settle, settle);
      else settle();
      return;
    }

    const upsellForm = source.closest('.hira-upsell__item');
    if (!upsellForm || !event.promise) return;
    const productId = upsellForm.dataset.productId;
    event.promise
      .then((result) => {
        if (result?.detail?.didError || frozen === null) return;
        frozen = withoutProduct(frozen, productId);
        restore();
      })
      .catch(() => {});
  });

  const watch = () => {
    const drawer = document.querySelector(DRAWER);
    if (!drawer) return;

    let wasOpen = isOpen();
    if (wasOpen) capture();

    new MutationObserver(() => {
      const open = isOpen();

      if (open && (!wasOpen || acceptNextRender)) {
        capture(); // just opened, or an outside add is still re-rendering it
      } else if (open) {
        restore();
      } else {
        if (wasOpen) refresh();
        frozen = null;
      }

      wasOpen = open;
    }).observe(drawer, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
